/**
 * Progress indicator following design brief
 * Minimal circle indicators - calm, non-gamified
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { theme } from '@core/theme';

export interface ProgressIndicatorConfig {
  totalScenarios: number;
  currentScenario: number;
  phase: number;
}

export class ProgressIndicator extends Container {
  private config: ProgressIndicatorConfig;
  private phaseLabel: Text;
  private dotsContainer: Container;

  constructor(config: ProgressIndicatorConfig) {
    super();

    this.config = config;

    // Phase label
    this.phaseLabel = new Text({
      text: `Phase ${config.phase}`,
      style: new TextStyle({
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.sizes.sm,
        fontWeight: theme.typography.weights.medium,
        fill: theme.colors.textMuted,
        letterSpacing: theme.typography.letterSpacing.wider,
      }),
    });
    this.addChild(this.phaseLabel);

    // Scenario dots
    this.dotsContainer = new Container();
    this.dotsContainer.position.set(0, 24);
    this.addChild(this.dotsContainer);

    this.draw();
  }

  private draw(): void {
    this.dotsContainer.removeChildren();

    const dotRadius = 5;
    const dotGap = 16;

    for (let i = 0; i < this.config.totalScenarios; i++) {
      const dot = new Graphics();

      let color: number;
      let filled = true;

      if (i < this.config.currentScenario) {
        // Completed
        color = theme.progressColors.completed;
      } else if (i === this.config.currentScenario) {
        // Current
        color = theme.progressColors.current;
      } else {
        // Upcoming
        color = theme.progressColors.upcoming;
        filled = false;
      }

      dot.circle(i * dotGap, 0, dotRadius);

      if (filled) {
        dot.fill(color);
      } else {
        dot.stroke({ width: 1.5, color });
      }

      this.dotsContainer.addChild(dot);
    }
  }

  update(currentScenario: number): void {
    this.config.currentScenario = currentScenario;
    this.draw();
  }

  setPhase(phase: number, totalScenarios: number): void {
    this.config.phase = phase;
    this.config.totalScenarios = totalScenarios;
    this.config.currentScenario = 0;
    this.phaseLabel.text = `Phase ${phase}`;
    this.draw();
  }
}
